import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bot, BotOff, Loader2, Save, Eye, EyeOff, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API = window.location.origin + "/api";

// Pannello admin per gestire lo script del chatbot LeadConnector
// in modo autonomo (senza redeploy). Persiste su `app_settings/chatbot`.
export default function ChatbotConfigPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [script, setScript] = useState("");
  const [active, setActive] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: "" });
  const [showRaw, setShowRaw] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/chatbot-config`, { withCredentials: true });
      setScript(data?.script || "");
      setActive(!!data?.active);
      setMeta({ updated_at: data?.updated_at || null, updated_by: data?.updated_by || "" });
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento config chatbot");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/chatbot-config`,
        { script: script.trim(), active },
        { withCredentials: true }
      );
      setScript(data?.script || "");
      setActive(!!data?.active);
      setMeta({ updated_at: data?.updated_at || null, updated_by: data?.updated_by || "" });
      setDirty(false);
      toast.success(active ? "Chatbot attivato e salvato" : "Configurazione salvata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Parsing leggero per anteprima (estrae src e widget id)
  const parsed = (() => {
    const m = (script || "").match(/<script\b([^>]*)>/i);
    if (!m) return null;
    const attrs = m[1] || "";
    const src = attrs.match(/\bsrc\s*=\s*["']([^"']+)["']/i)?.[1] || "";
    const wid = attrs.match(/\bdata-widget-id\s*=\s*["']([^"']+)["']/i)?.[1] || "";
    return { src, wid };
  })();

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-md border border-slate-100 flex items-center gap-3 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" /> Caricamento configurazione chatbot…
      </div>
    );
  }

  const lastUpdated = meta.updated_at
    ? meta.updated_at.slice(0, 19).replace("T", " ")
    : "—";

  return (
    <div
      className="bg-white rounded-3xl p-6 shadow-md border border-slate-100 space-y-5"
      data-testid="chatbot-config-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
            active && script ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
          }`}>
            {active && script ? <Bot className="w-5 h-5" /> : <BotOff className="w-5 h-5" />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Pannello Chatbot</h2>
            <p className="text-xs text-slate-500">
              Incolla qui lo snippet <code className="bg-slate-100 px-1 rounded">&lt;script&gt;</code> del widget LeadConnector.
              Visibile solo agli utenti <strong>non-admin</strong>.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
            <Switch
              id="chatbot-active"
              checked={active}
              onCheckedChange={(v) => { setActive(!!v); setDirty(true); }}
              data-testid="chatbot-active-switch"
            />
            <Label htmlFor="chatbot-active" className="text-xs font-semibold text-slate-700 cursor-pointer select-none">
              {active ? "Attivo" : "Disattivato"}
            </Label>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-[#3B42DD] hover:bg-[#2f36b8] text-white rounded-xl"
            data-testid="chatbot-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Salva
          </Button>
        </div>
      </div>

      {/* Editor snippet */}
      <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1.5">
            <Code2 className="w-3.5 h-3.5" /> Embed code chatbot
          </Label>
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="chatbot-toggle-raw"
          >
            {showRaw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showRaw ? "Nascondi raw" : "Mostra raw"}
          </button>
        </div>

        <textarea
          value={script}
          onChange={(e) => { setScript(e.target.value); setDirty(true); }}
          placeholder={'<script src="https://widgets.leadconnectorhq.com/loader.js"\n  data-resources-url="https://widgets.leadconnectorhq.com/chat-widget/loader.js"\n  data-widget-id="64f1...xxxxxx">\n</script>'}
          rows={showRaw ? 14 : 6}
          spellCheck={false}
          className="w-full font-mono text-xs leading-relaxed bg-white border border-slate-200 rounded-xl p-3 text-slate-700 resize-y focus:outline-none focus:ring-2 focus:ring-[#3B42DD]/40"
          data-testid="chatbot-script-textarea"
        />

        {/* Mini anteprima parsing */}
        {parsed ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
              <span className="text-slate-400 uppercase tracking-wider">src</span>
              <div className="font-mono text-slate-700 truncate" title={parsed.src} data-testid="chatbot-parsed-src">
                {parsed.src || "—"}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
              <span className="text-slate-400 uppercase tracking-wider">widget id</span>
              <div className="font-mono text-slate-700 truncate" title={parsed.wid} data-testid="chatbot-parsed-wid">
                {parsed.wid || "—"}
              </div>
            </div>
          </div>
        ) : script.trim() ? (
          <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            Snippet non riconosciuto. Verifica di aver incollato un tag <code>&lt;script src=... data-widget-id=...&gt;&lt;/script&gt;</code>.
          </div>
        ) : null}
      </div>

      {/* Metadati / istruzioni */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-blue-800 leading-relaxed">
          <strong>Come funziona:</strong> lo snippet salvato viene iniettato automaticamente nelle pagine
          (esclusi <code>/login</code>, <code>/monitor</code>, form pubblici). Cambia widget e salva: gli utenti
          vedranno il nuovo chatbot al prossimo refresh, <em>senza redeploy</em>.
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-slate-600 leading-relaxed">
          <strong className="text-slate-700">Ultima modifica:</strong> {lastUpdated}<br />
          <strong className="text-slate-700">Da:</strong> {meta.updated_by || "—"}<br />
          <strong className="text-slate-700">Stato:</strong>{" "}
          {active && script ? (
            <span className="text-emerald-700 font-semibold">Live per gli utenti</span>
          ) : active ? (
            <span className="text-amber-700 font-semibold">Attivo ma script vuoto</span>
          ) : (
            <span className="text-slate-500 font-semibold">Disattivato</span>
          )}
        </div>
      </div>
    </div>
  );
}
